import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import FieldRenderer from "./FieldRenderer";
import PasswordModal from "./PasswordModal";
import { getSanitizedPayload } from "./utils";

import TableLoader from "../TableComponent/TableLoader/TableLoader";
import DynamicButton from "../DynamicButton/DynamicButton";

import "react-toastify/dist/ReactToastify.css";
import "./EditFormComponent.css";

/* -------------------------------------------------------------------------- */
/*                                Helper Methods                              */
/* -------------------------------------------------------------------------- */

const formatModelName = (value = "") =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();

const extractCollection = (payload) => {
  if (Array.isArray(payload)) return payload;

  const possibleCollections = [
    payload?.data,
    payload?.results,
    payload?.docs,
    payload?.items,
    payload?.records,
  ];

  return possibleCollections.find(Array.isArray) || [];
};

const readResponseBody = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  try {
    if (contentType.includes("application/json")) {
      return await response.json();
    }

    const text = await response.text();

    return text
      ? {
          message: text,
        }
      : {};
  } catch {
    return {};
  }
};

/* -------------------------------------------------------------------------- */
/*                              Main Component                                */
/* -------------------------------------------------------------------------- */

const EditFormComponent = ({
  fields = [],
  formData = {},
  onChange,
  modelId,
  Put,
  file,
  model,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isCreate = !modelId;

  /* ------------------------------------------------------------------------ */
  /*                                  State                                   */
  /* ------------------------------------------------------------------------ */

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const [dropdownOptions, setDropdownOptions] = useState({});
  const [dropdownErrorCount, setDropdownErrorCount] = useState(0);

  const [allIds, setAllIds] = useState([]);

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [decryptedValue, setDecryptedValue] = useState("");

  /* ------------------------------------------------------------------------ */
  /*                              Derived Values                              */
  /* ------------------------------------------------------------------------ */

  const safeFields = useMemo(
    () =>
      Array.isArray(fields)
        ? fields.filter((field) => field && field.hidden !== true)
        : [],
    [fields],
  );

  const modelTitle = useMemo(() => formatModelName(model) || "Record", [model]);

  const isReadOnlyLink = useMemo(
    () => location.pathname.includes("/edit/LinkUaUcExecution"),
    [location.pathname],
  );

  const currentRecordIndex = useMemo(() => {
    if (!modelId || !allIds.length) return -1;

    return allIds.findIndex((recordId) => String(recordId) === String(modelId));
  }, [allIds, modelId]);

  const hasPreviousRecord = currentRecordIndex > 0;

  const hasNextRecord =
    currentRecordIndex >= 0 && currentRecordIndex < allIds.length - 1;

  const groupedFields = useMemo(() => {
    const sections = new Map();

    safeFields.forEach((field) => {
      const sectionName =
        typeof field.section === "string" && field.section.trim()
          ? field.section.trim()
          : "General Information";

      if (!sections.has(sectionName)) {
        sections.set(sectionName, {
          title: sectionName,
          description: field.sectionDescription || "",
          fields: [],
        });
      }

      sections.get(sectionName).fields.push(field);
    });

    return Array.from(sections.values());
  }, [safeFields]);

  const apiSignature = useMemo(() => {
    return JSON.stringify(
      safeFields
        .filter((field) => field.api)
        .map((field) => ({
          field: field.field || "",
          api: field.api,
        }))
        .sort((first, second) =>
          `${first.api}-${first.field}`.localeCompare(
            `${second.api}-${second.field}`,
          ),
        ),
    );
  }, [safeFields]);

  /* ------------------------------------------------------------------------ */
  /*                                Submission                                */
  /* ------------------------------------------------------------------------ */

  const handleSubmit = useCallback(
    async (event) => {
      event?.preventDefault();

      if (isSubmitting) return;

      if (!Put) {
        toast.error("Submission endpoint is not configured.");
        return;
      }

      setIsSubmitting(true);

      const requestUrl = isCreate ? Put : `${Put}/${modelId}`;
      const requestMethod = isCreate ? "POST" : "PUT";

      try {
        const payload = getSanitizedPayload(formData || {}, safeFields);

        const response = await fetch(requestUrl, {
          method: requestMethod,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await readResponseBody(response);

        if (!response.ok) {
          throw new Error(
            data?.message ||
              data?.error ||
              (isCreate
                ? "Unable to create the record."
                : "Unable to update the record."),
          );
        }

        toast.success(
          isCreate
            ? "Record created successfully."
            : "Changes saved successfully.",
          {
            position: "top-right",
            autoClose: 1600,
          },
        );

        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } catch (error) {
        console.error("Form submission error:", error);

        toast.error(
          error?.message || "An unexpected error occurred while saving.",
          {
            position: "top-right",
          },
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [Put, formData, isCreate, isSubmitting, modelId, onSuccess, safeFields],
  );

  /* ------------------------------------------------------------------------ */
  /*                        Previous / Next Record Data                        */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (isCreate || !Put) {
      setAllIds([]);
      return undefined;
    }

    const controller = new AbortController();

    const fetchRecordIds = async () => {
      try {
        const response = await fetch(Put, {
          signal: controller.signal,
        });

        const data = await readResponseBody(response);

        if (!response.ok) {
          throw new Error(data?.message || "Unable to load record navigation.");
        }

        const records = extractCollection(data);

        setAllIds(
          records
            .map((record) => record?._id)
            .filter(Boolean)
            .map(String),
        );
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error("Failed to load record IDs:", error);
        setAllIds([]);
      }
    };

    fetchRecordIds();

    return () => controller.abort();
  }, [Put, isCreate]);

  const goToPrevious = useCallback(() => {
    if (!hasPreviousRecord) {
      toast.info("You are already at the first record.");
      return;
    }

    const previousId = allIds[currentRecordIndex - 1];
    navigate(`/edit/${model}/${previousId}`);
  }, [allIds, currentRecordIndex, hasPreviousRecord, model, navigate]);

  const goToNext = useCallback(() => {
    if (!hasNextRecord) {
      toast.info("You are already at the last record.");
      return;
    }

    const nextId = allIds[currentRecordIndex + 1];
    navigate(`/edit/${model}/${nextId}`);
  }, [allIds, currentRecordIndex, hasNextRecord, model, navigate]);

  /* ------------------------------------------------------------------------ */
  /*                              Keyboard Save                               */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const handleKeyboardShortcut = (event) => {
      const isSaveShortcut =
        (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s";

      if (!isSaveShortcut) return;

      event.preventDefault();
      handleSubmit();
    };

    window.addEventListener("keydown", handleKeyboardShortcut);

    return () => {
      window.removeEventListener("keydown", handleKeyboardShortcut);
    };
  }, [handleSubmit]);

  /* ------------------------------------------------------------------------ */
  /*                            Dropdown Bootstrapping                         */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const controller = new AbortController();

    const fetchDropdownOptions = async () => {
      const apiFields = safeFields.filter((field) => field.api);

      if (!apiFields.length) {
        setDropdownErrorCount(0);
        setIsBootstrapping(false);
        return;
      }

      setIsBootstrapping(true);
      setDropdownErrorCount(0);

      /*
       * Group fields by API so that multiple fields using the same endpoint
       * only make one network request.
       */
      const fieldsByApi = new Map();

      apiFields.forEach((field) => {
        const existingFields = fieldsByApi.get(field.api) || [];
        existingFields.push(field);
        fieldsByApi.set(field.api, existingFields);
      });

      const loadedOptions = {};
      let errorCount = 0;

      const requests = Array.from(fieldsByApi.entries()).map(
        async ([api, consumerFields]) => {
          try {
            const response = await fetch(api, {
              signal: controller.signal,
            });

            const payload = await readResponseBody(response);

            if (!response.ok) {
              throw new Error(payload?.message || `Request failed for ${api}`);
            }

            const extractedOptions = extractCollection(payload);
            const options = extractedOptions.length
              ? extractedOptions
              : payload;

            loadedOptions[api] = options;

            consumerFields.forEach((field) => {
              if (field.field) {
                loadedOptions[field.field] = options;
              }
            });
          } catch (error) {
            if (error?.name === "AbortError") return;

            errorCount += 1;

            console.error(
              `Failed to load dropdown options from ${api}:`,
              error,
            );
          }
        },
      );

      await Promise.allSettled(requests);

      if (controller.signal.aborted) return;

      setDropdownOptions((previousOptions) => ({
        ...previousOptions,
        ...loadedOptions,
      }));

      setDropdownErrorCount(errorCount);
      setIsBootstrapping(false);
    };

    fetchDropdownOptions();

    return () => controller.abort();
  }, [apiSignature, safeFields]);

  /* ------------------------------------------------------------------------ */
  /*                             Password Actions                             */
  /* ------------------------------------------------------------------------ */

  const handlePasswordChange = useCallback(async () => {
    const passwordField = safeFields.find((field) => field.type === "password");

    if (!passwordField?.ChangePassword || !modelId) {
      toast.error("Password change endpoint is not configured.");
      return;
    }

    if (!newPassword.trim()) {
      toast.warning("Enter a new password.");
      return;
    }

    try {
      const response = await fetch(
        `${passwordField.ChangePassword}/${modelId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            newPassword,
          }),
        },
      );

      const data = await readResponseBody(response);

      if (!response.ok) {
        throw new Error(data?.message || "Unable to change password.");
      }

      toast.success("Password updated successfully.", {
        autoClose: 1400,
      });

      setNewPassword("");
      setShowPasswordChange(false);
    } catch (error) {
      console.error("Password change error:", error);

      toast.error(
        error?.message || "An error occurred while changing the password.",
      );
    }
  }, [modelId, newPassword, safeFields]);

  const handleDecrypt = useCallback(async () => {
    const plainPasswordField = safeFields.find(
      (field) => field.type === "plainPassword",
    );

    if (!plainPasswordField?.Decrypt || !modelId) {
      toast.error("Password decrypt endpoint is not configured.");
      return;
    }

    try {
      const response = await fetch(`${plainPasswordField.Decrypt}/${modelId}`);

      const data = await readResponseBody(response);

      if (!response.ok || !data?.plainPassword) {
        throw new Error(data?.message || "Unable to retrieve the password.");
      }

      setDecryptedValue(data.plainPassword);
    } catch (error) {
      console.error("Password decrypt error:", error);

      toast.error(
        error?.message || "An error occurred while decrypting the password.",
      );
    }
  }, [modelId, safeFields]);

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                  */
  /* ------------------------------------------------------------------------ */

  if (isBootstrapping) {
    return (
      <div className="edit-form edit-form--loading">
        <div className="edit-form-loader-card">
          <TableLoader />

          <div className="edit-form-loader-content">
            <h3>Preparing form</h3>
            <p>Loading form fields and supporting data.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-form">
      <form
        className="edit-form-card"
        onSubmit={handleSubmit}
        aria-busy={isSubmitting}
        noValidate
      >
        {/* Header */}
        <header className="edit-form-header">
          <div className="edit-form-header-main">
            <div className="edit-form-mode">
              <span
                className={`edit-form-mode-dot ${
                  isCreate
                    ? "edit-form-mode-dot--create"
                    : "edit-form-mode-dot--edit"
                }`}
              />

              {isCreate ? "New record" : "Record editor"}
            </div>

            <div className="edit-form-heading-row">
              <div>
                <h1 className="form-title">
                  {isCreate ? `Create ${modelTitle}` : `Edit ${modelTitle}`}
                </h1>

                <p className="edit-form-subtitle">
                  {isCreate
                    ? `Enter the required information to create a new ${modelTitle.toLowerCase()} record.`
                    : `Review and update the information for this ${modelTitle.toLowerCase()} record.`}
                </p>
              </div>

              <div className="edit-form-header-meta">
                {!isCreate && allIds.length > 0 && (
                  <span className="edit-form-record-position">
                    Record{" "}
                    {currentRecordIndex >= 0 ? currentRecordIndex + 1 : "—"} of{" "}
                    {allIds.length}
                  </span>
                )}

                <span className="edit-form-shortcut">
                  <kbd>
                    {navigator.platform?.includes("Mac") ? "⌘" : "Ctrl"}
                  </kbd>
                  <span>+</span>
                  <kbd>S</kbd>
                  <span>to save</span>
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Dropdown warning */}
        {dropdownErrorCount > 0 && (
          <div className="edit-form-warning" role="alert">
            <div className="edit-form-warning-icon">!</div>

            <div>
              <strong>Some supporting data could not be loaded.</strong>
              <p>
                {dropdownErrorCount} dropdown request
                {dropdownErrorCount > 1 ? "s" : ""} failed. You can still
                complete and submit the available fields.
              </p>
            </div>
          </div>
        )}

        {/* Form body */}
        <main className="form-body">
          {groupedFields.length > 0 ? (
            <div className="edit-form-sections">
              {groupedFields.map((section, sectionIndex) => (
                <section
                  key={`${section.title}-${sectionIndex}`}
                  className="edit-form-section"
                >
                  <div className="edit-form-section-header">
                    <div className="edit-form-section-number">
                      {String(sectionIndex + 1).padStart(2, "0")}
                    </div>

                    <div>
                      <h2>{section.title}</h2>

                      {section.description && <p>{section.description}</p>}
                    </div>
                  </div>

                  <div className="form-grid">
                    {section.fields.map((column, fieldIndex) => (
                      <div
                        key={column.field || `${column.label}-${fieldIndex}`}
                        className={[
                          "form-field-cell",
                          column.full ? "form-field-cell--full" : "",
                          column.half ? "form-field-cell--half" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <FieldRenderer
                          col={column}
                          formData={formData}
                          dropdownOptions={dropdownOptions}
                          decryptedValue={decryptedValue}
                          onChange={onChange}
                          setShowPasswordChange={setShowPasswordChange}
                          handleDecrypt={handleDecrypt}
                          isCreate={isCreate}
                          modelName={model}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="edit-form-empty">
              <div className="edit-form-empty-icon">+</div>
              <h3>No form fields configured</h3>
              <p>
                Add field definitions to the configuration to display this form.
              </p>
            </div>
          )}
        </main>

        {/* Sticky footer */}
        <footer className="form-actions">
          <div className="form-actions-left">
            {!isCreate && !isReadOnlyLink && (
              <div className="edit-form-nav-group">
                <DynamicButton
                  type="button"
                  icon="fa-chevron-left"
                  size="md"
                  variant="outline"
                  onClick={goToPrevious}
                  disabled={!hasPreviousRecord || isSubmitting}
                  title="Go to previous record"
                  aria-label="Go to previous record"
                />

                <div className="edit-form-nav-label">
                  <span>Record navigation</span>

                  <strong>
                    {currentRecordIndex >= 0
                      ? `${currentRecordIndex + 1} / ${allIds.length}`
                      : `— / ${allIds.length}`}
                  </strong>
                </div>

                <DynamicButton
                  type="button"
                  icon="fa-chevron-right"
                  size="md"
                  variant="outline"
                  onClick={goToNext}
                  disabled={!hasNextRecord || isSubmitting}
                  title="Go to next record"
                  aria-label="Go to next record"
                />
              </div>
            )}
          </div>

          <div className="form-actions-right">
            <div className="form-save-description">
              <span>{isCreate ? "Create record" : "Save changes"}</span>
              <small>
                {isCreate
                  ? "The new record will be added immediately."
                  : "Your updates will be applied to this record."}
              </small>
            </div>

            <button
              type="submit"
              className="btn-editForm-submit"
              disabled={isSubmitting || safeFields.length === 0}
            >
              {isSubmitting && (
                <span className="edit-form-button-spinner" aria-hidden="true" />
              )}

              <span>
                {isSubmitting
                  ? "Saving changes..."
                  : isCreate
                    ? "Create Record"
                    : "Save Changes"}
              </span>
            </button>
          </div>
        </footer>
      </form>

      {!isCreate && showPasswordChange && (
        <PasswordModal
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          onCancel={() => {
            setNewPassword("");
            setShowPasswordChange(false);
          }}
          onSave={handlePasswordChange}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={2200}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default EditFormComponent;
